import { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core'
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CPG_XWALK_QUERY_FIELDS, CPG_XWALK_SYSTEMS } from '@behuman/core-contract'
import { useClient, useOrpc } from '@/hooks/useClient'
import { SelectRecord } from '@/components/ui/SelectRecord'
import type { RecordColumn, RecordSearchParams } from '@/components/ui/SelectRecord'
import { toRecordQuery, toSearchResult } from './search'
import { actorLabel, useCpgFamilies, type CpgXwalkRuleRow } from './hooks'

const KIND_OPTIONS = [
  { value: 'condition', label: 'condition' },
  { value: 'observation', label: 'observation' },
  { value: 'medication', label: 'medication' },
]

const SYSTEM_OPTIONS = CPG_XWALK_SYSTEMS.map((s) => ({ value: s.uri, label: s.label }))

function AddRuleModal({
  familyId,
  onClose,
  onCreated,
}: {
  /** Preset and locked when opened from a family's detail panel. */
  familyId?: number
  onClose: () => void
  onCreated: () => void
}) {
  const orpc = useOrpc()
  const familiesQuery = useCpgFamilies()
  const [family, setFamily] = useState<string | null>(familyId ? String(familyId) : null)
  const [kind, setKind] = useState<string | null>('condition')
  const [system, setSystem] = useState<string | null>(SYSTEM_OPTIONS[0].value)
  const [code, setCode] = useState('')

  const familyOptions = useMemo(
    () =>
      (familiesQuery.data?.data ?? []).map((g) => ({
        group: g.label,
        items: g.families.map((f) => ({ value: String(f.id), label: `${f.label} (${f.slug})` })),
      })),
    [familiesQuery.data],
  )

  const create = useMutation(
    orpc.createCpgXwalkRule.mutationOptions({
      onSuccess: () => {
        onCreated()
        onClose()
      },
    }),
  )

  const ready = family !== null && kind !== null && system !== null && code.trim() !== ''

  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>Add matching rule</Text>}>
      <Stack gap="sm">
        <Select
          label="Family"
          data={familyOptions}
          value={family}
          onChange={setFamily}
          searchable
          disabled={familyId !== undefined}
        />
        <Select
          label="Kind"
          data={KIND_OPTIONS}
          value={kind}
          onChange={setKind}
          allowDeselect={false}
        />
        <Select
          label="Code system"
          data={SYSTEM_OPTIONS}
          value={system}
          onChange={setSystem}
          allowDeselect={false}
        />
        <TextInput
          label="Code"
          description="Matches this code plus its subtree (ICD-10/ATC by prefix; SNOMED via is-a closure once available)"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          placeholder="E11"
        />
        {create.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {create.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconPlus size={14} />}
            disabled={!ready}
            loading={create.isPending}
            onClick={() =>
              create.mutate({
                familyId: Number(family),
                kind: kind as 'condition' | 'observation' | 'medication',
                system: system!,
                code: code.trim(),
              })
            }
          >
            Add rule
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

interface XwalkRulesProps {
  /** Narrow to one family (the Families view's rules section); omitted = all rules. */
  familyId?: number
  className?: string
}

/**
 * The patient↔family matching rules table (cpg_family_patient_xwalk): filterable/orderable
 * on every server-indexed column, with add + delete. Every mutation lands an audit row.
 */
export function XwalkRules({ familyId, className }: XwalkRulesProps) {
  const client = useClient()
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [reloadKey, setReloadKey] = useState(0)
  const [adding, setAdding] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<CpgXwalkRuleRow | null>(null)

  const reload = useCallback(async () => {
    setReloadKey((k) => k + 1)
    // Rule counts on the family registry change with every add/delete.
    await queryClient.invalidateQueries({ queryKey: orpc.listCpgFamilies.key() })
  }, [queryClient, orpc])

  const remove = useMutation(
    orpc.deleteCpgXwalkRule.mutationOptions({
      onSuccess: async () => {
        setPendingDelete(null)
        await reload()
      },
    }),
  )

  const columns = useMemo<RecordColumn<CpgXwalkRuleRow>[]>(() => {
    const base: RecordColumn<CpgXwalkRuleRow>[] = [
      { key: 'id', header: '#', width: 60 },
      ...(familyId === undefined
        ? [{ key: 'familySlug', header: 'Family', width: 170 } as RecordColumn<CpgXwalkRuleRow>]
        : []),
      { key: 'kind', header: 'Kind', width: 110 },
      { key: 'system', header: 'System', width: 240 },
      { key: 'code', header: 'Code', width: 100 },
      {
        key: 'updatedBy',
        header: 'Updated by',
        width: 150,
        display: (r) => actorLabel(r.updatedBy),
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        width: 130,
        display: (r) => new Date(r.updatedAt).toLocaleDateString(),
      },
      {
        // The delete affordance rides on a synthetic key; it is neither sortable nor filterable.
        key: 'familyLabel',
        header: '',
        width: 50,
        sortable: false,
        filterable: false,
        display: () => '',
        render: (r) => (
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            aria-label={`Delete rule ${r.id}`}
            onClick={(e) => {
              e.stopPropagation()
              setPendingDelete(r)
            }}
          >
            <IconTrash size={14} />
          </Button>
        ),
      },
    ]
    return base
  }, [familyId])

  const search = useCallback(
    async (params: RecordSearchParams<CpgXwalkRuleRow>) => {
      const res = await client.listCpgXwalkRules({
        ...toRecordQuery(params, CPG_XWALK_QUERY_FIELDS),
        familyId,
      })
      return toSearchResult(res)
    },
    [client, familyId],
  )

  return (
    <div className={`flex h-full min-h-0 flex-col ${className ?? ''}`}>
      <Group justify="flex-end" className="shrink-0 border-b border-gray-200 px-3 py-1.5">
        <Button
          size="compact-sm"
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={() => setAdding(true)}
        >
          Add rule
        </Button>
      </Group>
      <div className="min-h-0 flex-1">
        <SelectRecord<CpgXwalkRuleRow>
          key={reloadKey}
          columns={columns}
          getRowId={(r) => String(r.id)}
          search={search}
          onSelect={() => {}}
          recordNoun="rule"
          slug={familyId === undefined ? 'cpg-xwalk' : 'cpg-xwalk-family'}
          pageSize={40}
          showGlobalSearch={false}
          showSelectionBar={false}
          footerHint={null}
        />
      </div>

      {adding && (
        <AddRuleModal familyId={familyId} onClose={() => setAdding(false)} onCreated={reload} />
      )}

      <Modal
        opened={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title={<Text fw={600}>Delete matching rule</Text>}
      >
        {pendingDelete && (
          <Stack gap="sm">
            <Text size="sm">
              Delete rule #{pendingDelete.id} — <strong>{pendingDelete.familySlug}</strong> ·{' '}
              {pendingDelete.kind} · {pendingDelete.code}? Patients matched only through this code
              (and its subtree) will leave the family on the next evaluation.
            </Text>
            {remove.error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />}>
                {remove.error.message}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setPendingDelete(null)}>
                Cancel
              </Button>
              <Button
                color="red"
                loading={remove.isPending}
                onClick={() => remove.mutate({ id: pendingDelete.id })}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  )
}
